import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import {
  FileSpreadsheet,
  Plus,
  Upload,
  Download,
  Eye,
  Edit,
  Trash2,
  CheckCircle2,
  FileText,
  Table,
  Settings,
  Loader2 } from
'lucide-react';

import { oigAPI } from '@/shared/services/api';

export const CSVImport = () => {const { t } = useTranslation("oig");
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoading(true);
        const data = await oigAPI.getCSVTemplates(); // Let's check if this is in api.js, otherwise I'll add it.
        setTemplates(data || []);
      } catch (err) {
        console.error('Failed to fetch CSV templates:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchTemplates();
  }, []);

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(selectedFiles.map((f) => ({
      name: f.name,
      size: f.size,
      status: 'pending'
    })));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
      </div>);

  }

  return (
    <div className="space-y-6" data-testid="oig-csv-import">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk'] flex items-center gap-3">
            <FileSpreadsheet className="h-8 w-8 text-orange-600" />{t("importaoCsvtxt", "Importa\xE7\xE3o CSV/TXT")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("importaoEmMassa", "Importa\xE7\xE3o em massa de dados via arquivos CSV e TXT")}

          </p>
        </div>
        <Button className="bg-orange-600 hover:bg-orange-700 gap-2">
          <Plus className="h-4 w-4" />{t("novoTemplate", "Novo Template")}

        </Button>
      </div>

      {/* Templates Grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {templates.map((template) =>
        <Card
          key={template.id}
          className={`card-elevated hover:shadow-lg transition-all cursor-pointer ${selectedTemplate === template.id ? 'ring-2 ring-orange-500' : ''}`
          }
          onClick={() => setSelectedTemplate(template.id)}>
          
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-orange-100">
                    <Table className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <CardTitle className="text-base">{template.nome}</CardTitle>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">{template.descricao}</p>

              <div className="space-y-2 mb-4">
                <p className="text-xs font-medium text-muted-foreground">{t("campos", "Campos:")}</p>
                <div className="flex flex-wrap gap-1">
                  {(template.campos || template.columns || []).map((campo, i) =>
                <Badge key={i} variant="secondary" className="text-xs">
                      {campo}
                    </Badge>
                )}
                </div>
              </div>

              <div className="pt-3 border-t border-border space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{t("registrosImportados", "Registros importados:")}</span>
                  <span className="font-medium text-orange-600">{(template.registrosImportados || 0).toLocaleString()}</span>
                </div>
                <p className="text-xs text-muted-foreground">{t("ltimoUso", "\xDAltimo uso:")}
                {template.ultimoUso ? new Date(template.ultimoUso).toLocaleDateString('pt-BR') : 'N/A'}
                </p>
              </div>

              <div className="flex gap-2 pt-4">
                <Button variant="outline" size="sm" className="flex-1 gap-1">
                  <Download className="h-3 w-3" />{t("baixar", "Baixar")}

              </Button>
                <Button variant="outline" size="sm" className="flex-1 gap-1">
                  <Settings className="h-3 w-3" />{t("editar", "Editar")}

              </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Upload Section */}
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5 text-muted-foreground" />{t("uploadDeArquivo", "Upload de Arquivo")}

          </CardTitle>
          <CardDescription>
            {selectedTemplate ?
            `Template selecionado: ${templates.find((t) => t.id === selectedTemplate)?.nome}` :
            'Selecione um template acima antes de fazer upload'
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className={`border-2 border-dashed rounded-lg p-8 text-center ${selectedTemplate ? 'border-orange-300 bg-orange-50/30' : 'border-gray-300 opacity-50'}`
          }>
            <FileSpreadsheet className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600 mb-2">{t("arrasteArquivosCsv", "Arraste arquivos CSV ou TXT aqui")}</p>
            <p className="text-sm text-muted-foreground mb-4">{t("ouCliquePara", "ou clique para selecionar")}

            </p>
            <input
              type="file"
              id="csv-upload"
              className="hidden"
              multiple
              accept=".csv,.txt"
              onChange={handleFileSelect}
              disabled={!selectedTemplate} />
            
            <label htmlFor="csv-upload">
              <Button variant="outline" disabled={!selectedTemplate} asChild>
                <span className="cursor-pointer">{t("selecionarArquivos", "Selecionar Arquivos")}</span>
              </Button>
            </label>
          </div>

          {files.length > 0 &&
          <div className="mt-6 space-y-4">
              <h3 className="font-medium">{t("arquivosParaImportar", "Arquivos para importar:")}</h3>
              {files.map((file, i) =>
            <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-orange-600" />
                    <div>
                      <p className="font-mono text-sm">{file.name}</p>
                      <p className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(1)}{t("kb", "KB")}</p>
                    </div>
                  </div>
                  <Badge variant="outline">{t("pendente", "Pendente")}</Badge>
                </div>
            )}
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setFiles([])}>{t("cancelar", "Cancelar")}

              </Button>
                <Button className="bg-orange-600 hover:bg-orange-700 gap-2">
                  <CheckCircle2 className="h-4 w-4" />{t("iniciarImportao", "Iniciar Importa\xE7\xE3o")}

              </Button>
              </div>
            </div>
          }
        </CardContent>
      </Card>

      {/* Field Mapping Preview */}
      {selectedTemplate &&
      <Card className="card-elevated">
          <CardHeader>
            <CardTitle>{t("mapeamentoDeCampos", "Mapeamento de Campos")}</CardTitle>
            <CardDescription>{t("configureComoOs", "Configure como os campos do CSV ser\xE3o mapeados para o sistema")}

          </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-orange-100">
                  <tr>
                    <th className="py-2 px-4 text-left font-medium">{t("colunaCsv", "Coluna CSV")}</th>
                    <th className="py-2 px-4 text-left font-medium">{t("campoSistema", "Campo Sistema")}</th>
                    <th className="py-2 px-4 text-left font-medium">{t("tipo", "Tipo")}</th>
                    <th className="py-2 px-4 text-left font-medium">{t("obrigatrio", "Obrigat\xF3rio")}</th>
                  </tr>
                </thead>
                <tbody>
                  {(templates.find((t) => t.id === selectedTemplate)?.campos || templates.find((t) => t.id === selectedTemplate)?.columns || []).map((campo, i) =>
                <tr key={i} className="border-b border-border">
                      <td className="py-3 px-4 font-mono text-sm">{campo}</td>
                      <td className="py-3 px-4 text-sm">{campo.toLowerCase().replace(/ /g, '_')}</td>
                      <td className="py-3 px-4">
                        <Badge variant="outline" className="text-xs">{t("string", "String")}</Badge>
                      </td>
                      <td className="py-3 px-4">
                        <Badge className={i < 3 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}>
                          {i < 3 ? t("sim", "Sim") : t("nao", "N\xE3o")}
                        </Badge>
                      </td>
                    </tr>
                )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      }
    </div>);

};